import { useState, useEffect, FC } from "react";
import { Colors } from "constants/constants";
import { Box, Typography } from "@material-ui/core";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { handleGetRoles } from "pages/Scorecard/requests";
import { DashboardMainChartType } from "types/Dashboard";
import { useDateWeekSelector } from "hooks/useDateWeekSelector";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import { daRoles } from "mocks/roleDA";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

type ElementData = { counter: number; title: string; role: number };

export const RolesChart: FC<DashboardMainChartType> = ({ station }) => {
  const [loading, setLoading] = useState(false);
  const [loadingPast, setLoadingPast] = useState(false);
  const [requestData, setRequestData] = useState<ElementData[]>([]);
  const [requestPastData, setRequestPastData] = useState<ElementData[]>([]);
  const { perfomanceSettings } = useTypeSelectors((state) => state.perfomanceScores);

  const { date, selectType } = perfomanceSettings;

  const { pastMonthDate, pastWeekDate } = useDateWeekSelector({
    defaultWeek: date?.week,
    defaultMonth: date?.month,
  });

  const splitMonthDate = pastMonthDate.split(".");
  const splitWeekDate = pastWeekDate.split(".");
  const splitPastMonth = +splitMonthDate[0];
  const splitPastMonthYear = +splitMonthDate[1];
  const splitPastWeek = +splitWeekDate[0];
  const splitPastWeekYear = +splitWeekDate[1];

  useEffect(() => {
    if (selectType !== "daily") {
      const params = {
        station,
        year: +date?.year,
        ...(selectType === "month" ? { month: +date?.month } : { week: +date?.week }),
      };
      const pastParams = {
        station,
        year: selectType === "month" ? splitPastMonthYear : splitPastWeekYear,
        ...(selectType === "month" ? { month: splitPastMonth } : { week: splitPastWeek }),
      };

      handleGetRoles(params, setRequestData, setLoading);
      handleGetRoles(pastParams, setRequestPastData, setLoadingPast);
    }
  }, [
    station,
    date?.year,
    date?.month,
    date?.week,
    selectType,
    splitPastMonth,
    splitPastMonthYear,
    splitPastWeek,
    splitPastWeekYear,
  ]);

  const roles = [
    daRoles.training,
    daRoles.quality,
    daRoles.rescue,
    daRoles.standby,
    daRoles.special,
    daRoles.driver,
  ].map((role) => ({
    name: role.name,
    id: role.roleId,
  }));

  const labels = roles.map((el) => el.name);
  const newData: number[] = roles.map((role) => {
    const matchingData = requestData.find((dataItem: ElementData) => role.id === dataItem.role);
    return matchingData?.counter ?? 0;
  });
  const pastData: number[] = roles.map((role) => {
    const matchingData = requestPastData.find((dataItem: ElementData) => role.id === dataItem.role);
    return matchingData?.counter ?? 0;
  });

  const dataChart = {
    labels,
    datasets:
      selectType !== "daily"
        ? [
            {
              label: "Current",
              data: newData,
              backgroundColor: Colors.PASTEL_BLUE,
              minBarLength: 3,
              barThickness: 15,
              borderRadius: 25,
            },
            {
              label: "Past",
              data: pastData,
              backgroundColor: Colors.PASTEL_GREEN,
              minBarLength: 3,
              barThickness: 15,
              borderRadius: 25,
            },
          ]
        : [],
  };

  return (
    <Box
      style={{
        display: "flex",
        width: "100%",
        boxSizing: "border-box",
        position: "relative",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {!loading && !loadingPast && (
        //@ts-ignore
        <Bar options={options} data={dataChart} />
      )}
      <Box style={{ display: "flex", justifyContent: "center", gap: "20px" }}>
        <Box
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "5px",
            alignItems: "center",
          }}
        >
          <Box
            style={{
              width: "20px",
              height: "20px",
              background: Colors.PASTEL_BLUE,
              display: "block",
            }}
          />
          <Typography variant="body2">Current data</Typography>
        </Box>
        <Box
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "5px",
            alignItems: "center",
          }}
        >
          <Box
            style={{
              width: "20px",
              height: "20px",
              background: Colors.PASTEL_GREEN,
              display: "block",
            }}
          />
          <Typography variant="body2">Past data</Typography>
        </Box>
      </Box>
    </Box>
  );
};

const options = {
  responsive: true,
  scale: {
    ticks: {
      precision: 0,
    },
  },
  scales: {
    y: {
      title: {
        display: false,
        text: "Value",
      },
      min: 0,
      ticks: {
        color: `${Colors.MAIN}`,
        font: {
          weight: "700",
        },
      },
    },
    x: {
      grid: {
        display: false,
      },
      ticks: {
        color: `${Colors.MAIN}`,
        font: {
          weight: "700",
        },
      },
    },
  },
  plugins: {
    legend: {
      position: "top" as const,
      display: false,
    },
    title: {
      display: true,
      text: "Roles",
    },
  },
};
